import './MapLayerSwitch.scss'

import { HDSToggle } from '@here/hds-react-components'
import classNames from 'classnames'
import useTheme, { Theme } from 'components/App/hooks/useTheme'
import { IzumiMessageKey } from 'izumi/IzumiMessageKey'
import { config } from 'lib/config'
import { i18n } from 'lib/i18n'
import { BaseLayerNames, getMapLayerName } from 'map/layers'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface MapLayerSwitchProps {
  baseLayerName: BaseLayerNames
  isTrafficEnabled: boolean
  onBaseLayerChange: (layerName: BaseLayerNames) => void
  onTrafficChange: (isEnabled: boolean) => void
}

type BaseLayerItems = {
  [name in BaseLayerNames.OMV | BaseLayerNames.SATELLITE]: {
    i18nLabelKey: IzumiMessageKey
    thumbnails: {
      highRes: string
      lowRes: string
    }
  }
}

const i18nBaseKey = 'map-controls.layer-switcher'
const thumbnailBasePath = `${config.ROOT_PATH}images/layer-thumbnails`
const baseLayerItems: BaseLayerItems = {
  [BaseLayerNames.OMV]: {
    i18nLabelKey: `${i18nBaseKey}.day`,
    thumbnails: {
      highRes: `${thumbnailBasePath}/map-layer-omv@2x.png`,
      lowRes: `${thumbnailBasePath}/map-layer-omv.png`,
    },
  },
  [BaseLayerNames.SATELLITE]: {
    i18nLabelKey: `${i18nBaseKey}.satellite`,
    thumbnails: {
      highRes: `${thumbnailBasePath}/map-layer-satellite@2x.png`,
      lowRes: `${thumbnailBasePath}/map-layer-satellite.png`,
    },
  },
}

export default function MapLayerSwitch({
  baseLayerName,
  isTrafficEnabled,
  onBaseLayerChange,
  onTrafficChange,
}: MapLayerSwitchProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const [isCollapsed, setCollapsed] = useState(true)
  const [_, setTheme] = useTheme()

  const handleDocumentClick = useCallback(
    ({ target }: Event): void => {
      if (
        !isCollapsed &&
        document.contains(target as HTMLElement) &&
        !(ref.current as HTMLDivElement).contains(target as HTMLElement)
      ) {
        setCollapsed(true)
      }
    },
    [isCollapsed]
  )

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick)
    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  })

  const handleBaseLayerChange = useCallback(
    (mapLayer: BaseLayerNames) => {
      if (isTrafficEnabled) {
        onBaseLayerChange(
          mapLayer === BaseLayerNames.OMV
            ? BaseLayerNames.TRAFFIC
            : BaseLayerNames.SATELLITE_TRAFFIC
        )
      } else {
        onBaseLayerChange(mapLayer)
      }

      if (
        mapLayer === BaseLayerNames.OMV ||
        mapLayer === BaseLayerNames.TRAFFIC ||
        mapLayer === BaseLayerNames.NORMAL
      ) {
        setTheme(Theme.light)
      } else {
        setTheme(Theme.hybrid)
      }
    },
    [isTrafficEnabled, onBaseLayerChange, setTheme]
  )

  return (
    <div className="MapLayerSwitch" ref={ref}>
      {isCollapsed ? (
        <img
          className="MapLayerSwitch__thumbnail"
          src={
            getMapLayerName(baseLayerName) === 'omv'
              ? baseLayerItems.satellite.thumbnails.lowRes
              : baseLayerItems.omv.thumbnails.lowRes
          }
          srcSet={
            getMapLayerName(baseLayerName) === 'omv'
              ? `${baseLayerItems.satellite.thumbnails.highRes} 2x`
              : `${baseLayerItems.omv.thumbnails.highRes} 2x`
          }
          onClick={() => setCollapsed(false)}
        />
      ) : (
        <div className="MapLayerSwitch__card">
          <div className="MapLayerSwitch__list">
            {Object.entries(baseLayerItems).map(([name, { i18nLabelKey, thumbnails }]) => (
              <div
                key={name}
                className={classNames('MapLayerSwitch__item', {
                  'MapLayerSwitch__item--active': name === getMapLayerName(baseLayerName),
                })}
                onClick={() => handleBaseLayerChange(name as BaseLayerNames)}
              >
                <div className="MapLayerSwitch__thumbnail-wrapper">
                  <img
                    className="MapLayerSwitch__thumbnail"
                    src={thumbnails.lowRes}
                    srcSet={`${thumbnails.highRes} 2x`}
                  />
                </div>

                <span className="MapLayerSwitch__item-label">{i18n(i18nLabelKey)}</span>
              </div>
            ))}
          </div>

          <div className="MapLayerSwitch__toggle-container">
            <div className="MapLayerSwitch__traffic-toggle">
              <span className="MapLayerSwitch__traffic-toggle-label">
                {i18n(`${i18nBaseKey}.traffic`)}
              </span>

              <HDSToggle
                value="traffic-layer"
                checked={isTrafficEnabled}
                onCheck={() => onTrafficChange(Boolean(!isTrafficEnabled))}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
